import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPage,
  IonRippleEffect,
  IonRow,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import { chevronBackOutline } from 'ionicons/icons'
import React, { BaseSyntheticEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import LabelComponent from '../../../components/label/Label.component'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { transformDateTimeToMonthDayYear, truncateSecondsIfNecessary } from '../../../utilities/dateTime.util'
import { preventPageScroll } from '../../../utilities/preventPageScroll.util'
import { convertFirstLetterToUpperCase } from '../../../utilities/string.util'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { IConfigurationsState, IServiceConfig } from '../../configurations/store/configurations.types'
import { getCadImportOptionsSelector } from '../../options/selectors'
import { resetCadRecords } from '../../options/store/options.actions'
import CadMultiSelectModal from '../CadMultiSelectModal/CadMultiSelectModal.component'
import { getCadImportSelector } from '../selectors'
import { getCadRecords, resetCadImportState, setDisableUnitFilteringFlag } from '../store/cadImport.actions'
import { CadRecord, CadRecordDataPoints } from '../store/cadImport.types'
import './CadImport.page.css'
import { ToastColor } from '../../../components/toast/store/toast.constants'
import { getToastProps } from '../../../components/toast/toastIcon.util'
import { getPatientInformationState } from '../../patientInformation/selectors'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'

interface ModalState {
  showModal: boolean;
  cadData: CadRecordDataPoints;
}

const CadImport: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const cadImport = useSelector(getCadImportSelector)
  const { failure, responseMessage, importSuccess, isLoading, disableUnitFilteringFlag, userCanDisableUnitFilter } = cadImport
  const cadImportOptions = useSelector(getCadImportOptionsSelector)
  const { data } = cadImportOptions
  const configurationsState: IConfigurationsState = useSelector(getConfigurationsSelector)
  const { cadImportConfig } = configurationsState

  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const [showToast, setShowToast] = useState(true)
  const [modalState, setShowModal] = useState<ModalState>({ showModal: false, cadData: [] })
  const { searchImport } = useSelector(getPatientInformationState)

  const onCloseClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    dispatch(resetCadImportState())
    history.push(TabConstants.CHART_ROUTE)
  }

  const showMultiSelectModal = (cadData: CadRecordDataPoints) => {
    preventPageScroll(true)
    if (searchImport) {
      cadData.map(dataPoint => {
        if (dataPoint.id.startsWith('PT_'))
          dataPoint.performImport = false
      })
    }
    setShowModal({ showModal: true, cadData: cadData })
  }

  const resetModalState = () => {
    setShowModal({ showModal: false, cadData: [] })
    preventPageScroll(false)
  }

  const setDisableUnitFilteringOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(resetCadRecords())
    dispatch(setDisableUnitFilteringFlag(e.detail.checked))
    dispatch(getCadRecords())
  }

  useIonViewWillEnter(() => {
    if (cadImportConfig.filter_unit === 0) {
      dispatch(setDisableUnitFilteringFlag(true))
    }
    dispatch(getCadRecords())
    return
  })

  const getStreetAddressOfSceneOfCall = (record: CadRecord): string => {
    return record.cadData.find((i) => i.id === 'REF_ADDR') ||
      record.cadData.find((i) => i.id === 'REF_CITY') ||
      record.cadData.find((i) => i.id === 'REF_STATE') ||
      record.cadData.find((i) => i.id === 'REF_ZIP') ?
      `${record.cadData.find((i) => i.id === 'REF_ADDR') ? record.cadData.find((i) => i.id === 'REF_ADDR')?.value : ''} 
      ${record.cadData.find((i) => i.id === 'REF_CITY') ? record.cadData.find((i) => i.id === 'REF_CITY')?.value?.name : ''}, 
      ${record.cadData.find((i) => i.id === 'REF_STATE') ? record.cadData.find((i) => i.id === 'REF_STATE')?.value?.id : ''} 
      ${record.cadData.find((i) => i.id === 'REF_ZIP') ? record.cadData.find((i) => i.id === 'REF_ZIP')?.value : ''}`
      : 'Not Available'
  }

  const getDispatchId = (record: CadRecord): string => {
    return record.cadData.find((i) => i.id === 'DISPATCH_RECORD_ID')?.value
  }

  const getPatientName = (record: CadRecord): string => {
    return record.cadData.find((i) => i.id === 'PT_FNAME') || record.cadData.find((i) => i.id === 'PT_LNAME') ?
      `${record.cadData.find((i) => i.id === 'PT_FNAME') ? convertFirstLetterToUpperCase(record.cadData.find((i) => i.id === 'PT_FNAME')?.value) : ''} 
      ${record.cadData.find((i) => i.id === 'PT_LNAME') ? convertFirstLetterToUpperCase(record.cadData.find((i) => i.id === 'PT_LNAME')?.value) : ''}`
      : 'Not Available'
  }

  const getDOB = (record: CadRecord): string => {
    return record.cadData.find((i) => i.id === 'PT_DOB')
      ? transformDateTimeToMonthDayYear(record.cadData.find((i) => i.id === 'PT_DOB')?.value)
      : 'Not Available'
  }

  const getUnit = (record: CadRecord): string => {
    return record.cadData.find((i) => i.id === 'UNIT') ? record.cadData.find((i) => i.id === 'UNIT')?.value?.name : 'Not Available'
  }

  const getDispatchDateTime = (record: CadRecord): string => {
    const date = record.cadData.find((i) => i.id === 'DT_DISPATCH_DATE')
    const time = record.cadData.find((i) => i.id === 'DT_DISPATCH_TIME')
    return (date?.value && time?.value) ? `${date.value} ${truncateSecondsIfNecessary(time.value, displaySeconds)}` : 'Not Available'
  }

  return (
    <IonPage id='CadImportPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Close' fill='clear' onClick={onCloseClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Close')}
            </IonButton>
          </IonButtons>
          <IonTitle>CAD Import</IonTitle>
        </IonToolbar>
      </IonHeader>
      {importSuccess && (
        <IonToast
          isOpen={showToast}
          position='top'
          message={'Successfully imported CAD data'}
          {...getToastProps(ToastColor.Success)}
          duration={2000}
          onDidDismiss={() => setShowToast(false)}
        />
      )}
      <IonContent fullscreen color='light'>
        <IonList className='list-margin-bottom'>
          {cadImportConfig.filter_unit === 1 && userCanDisableUnitFilter && (
            <IonItem lines='none' className='cad-unit-toggle'>
              <IonLabel>{t('cadImport.disableUnitFiltering')}</IonLabel>
              <IonToggle
                slot='start'
                data-testid='DisableUnitFiltering'
                checked={disableUnitFilteringFlag}
                onIonChange={(e) => setDisableUnitFilteringOnChange(e)}
              />
            </IonItem>
          )}
          {data.map((record: CadRecord) => (
            <IonItem
              button
              detail={false}
              data-testid={`CadRecord_${record.cadData.find((i) => i.id === 'ID')?.value}`}
              key={`CadRecord_${record.cadData.find((i) => i.id === 'ID')?.value}`}
              onClick={() => showMultiSelectModal(record.cadData)}
              className='ion-no-padding ion-activatable'>
              <IonGrid>
                <IonRow className='ion-align-items-start'>
                  <IonCol size='8'>
                    <IonLabel color='medium'>
                      <h3>{t('cadImport.unit')}</h3>
                    </IonLabel>
                    <LabelComponent isPHI className='text-wrap' label={getUnit(record)} />
                  </IonCol>
                  <IonCol>
                    <IonLabel color='medium'>
                      <h3>{t('cadImport.dispatchId')}</h3>
                    </IonLabel>
                    <LabelComponent isPHI label={getDispatchId(record)} />
                  </IonCol>
                </IonRow>
                <IonRow className='ion-align-items-start'>
                  <IonCol size='8'>
                    <IonLabel color='medium'>
                      <h3>{t('cadImport.streetAddressOfSceneOfCall')}</h3>
                    </IonLabel>
                    <LabelComponent isPHI className='text-wrap' label={getStreetAddressOfSceneOfCall(record)} />
                  </IonCol>
                  <IonCol>
                    <IonLabel color='medium'>
                      <h3>{t('cadImport.dispatchDateTime')}</h3>
                    </IonLabel>
                    <LabelComponent isPHI label={getDispatchDateTime(record)} />
                  </IonCol>
                </IonRow>
                <IonRow className='ion-align-items-start'>
                  <IonCol size='8'>
                    <IonLabel color='medium'>
                      <h3>{t('cadImport.nature')}</h3>
                    </IonLabel>
                    <IonLabel>
                      {record.cadData.find((i) => i.id === 'MEDICAL_CATEGORY')
                        ? record.cadData.find((i) => i.id === 'MEDICAL_CATEGORY')?.value?.name
                        : 'Not Available'}
                    </IonLabel>
                  </IonCol>
                </IonRow>
                <IonRow className='ion-align-items-start'>
                  <IonCol size='8'>
                    <IonLabel color='medium'>
                      <h3>{t('cadImport.patientName')}</h3>
                    </IonLabel>
                    <LabelComponent isPHI label={getPatientName(record)} />
                  </IonCol>
                  <IonCol>
                    <IonLabel color='medium'>
                      <h3>{t('cadImport.dob')}</h3>
                    </IonLabel>
                    <LabelComponent isPHI label={getDOB(record)} />
                  </IonCol>
                </IonRow>
              </IonGrid>
              <IonRippleEffect />
            </IonItem>
          ))}

          <CadMultiSelectModal cadData={modalState.cadData} showModal={modalState.showModal} resetModal={resetModalState} />

          {failure && (
            <IonItem data-testid='SubmitChartFailure'>
              <IonCol>
                <IonRow class='ion-padding'>
                  <IonLabel class='ion-text-wrap'>Please fix the following issue(s):</IonLabel>
                </IonRow>
                <IonRow class='ion-padding'>
                  <IonLabel class='ion-text-wrap'>{responseMessage}</IonLabel>
                </IonRow>
              </IonCol>
            </IonItem>
          )}
        </IonList>
        {isLoading && <IonLoading data-testid='Loader' isOpen animated showBackdrop spinner={'crescent'} />}
      </IonContent>
    </IonPage>
  )
}

export default CadImport
